/**
 * Billing — the tenant's collection surface (prepaid AND postpaid).
 *
 * A first-class page split out of Usage: a summary stat row (balance/spend +
 * plan), the payment method, the prepaid credit top-up (prepaid tenants only)
 * and the invoice history with hosted-invoice + PDF links. Everything routes
 * to the Stripe Customer Portal for the heavy lifting (payment methods, full
 * invoice archive), so the console owns only the at-a-glance snapshot and the
 * two self-serve actions (top-up, open portal).
 *
 * Money is shown in USD via ``formatUsdCents`` — this is the DEVELOPER/account
 * surface, technical metering for the integrator; the markup % never crosses
 * the wire and is NEVER rendered here (customer-facing "% only" rule is a
 * different surface).
 */

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Building2,
  CreditCard,
  ExternalLink,
  FileDown,
  FileText,
  Mail,
  MapPin,
  ReceiptText,
  Wallet,
} from "lucide-react";
import type * as React from "react";
import { useState } from "react";

import { cn } from "@ando/ui";

import {
  consoleInvoicePdfUrl,
  getConsoleErrorMessage,
  startConsoleBillingPortal,
  startConsoleCreditCheckout,
  updateConsoleWalletScope,
} from "../api/client";
import {
  consoleBillingSummaryQueryOptions,
  consoleCreditsQueryOptions,
  consoleInvoicesQueryOptions,
  consoleQueryKeys,
  consoleWalletScopeQueryOptions,
} from "../api/queries";
import { ConsoleSectionBoundary } from "../components/ConsoleSectionBoundary";
import { InvoiceStatusBadge } from "../components/StatusBadges";
import { TopupReturnStatus } from "../components/TopupReturnStatus";
import {
  ConBadge,
  ConButton,
  ConCard,
  ConCardHeader,
  ConEmptyState,
  ConErrorNote,
  ConFieldStatus,
  ConInput,
  ConPage,
  ConSkeleton,
  ConSkeletonRows,
  ConStat,
  ConTHead,
  ConTable,
  ConTr,
  conFocusRing,
  conTdClass,
  conTdNumClass,
  conThClass,
  conThNumClass,
} from "../components/atoms";
import { formatDate, formatUsdCents } from "../lib/format";
import { consumeTopupAttempt, parseCustomTopupUsd, topupAttemptIdempotencyKey } from "../lib/getStarted";
import { consoleToast } from "../lib/toast";

/**
 * The Stripe Customer Portal session — minted on demand, then the browser is
 * sent to it. Shared across the header CTA and both payment-method buttons so
 * a single in-flight state guards every entrypoint (no double-mint).
 */
function useBillingPortal() {
  return useMutation({
    mutationFn: (flow: "manage" | "payment_method_update") =>
      startConsoleBillingPortal(window.location.href, flow),
    onSuccess: (url) => {
      if (url && url !== "#mock-portal") window.location.href = url;
    },
    onError: (err) => consoleToast.error(getConsoleErrorMessage(err)),
  });
}

type BillingPortal = ReturnType<typeof useBillingPortal>;

export function ConsoleBillingPage(): React.ReactElement {
  const { i18n } = useLingui();
  const portal = useBillingPortal();
  const credits = useQuery(consoleCreditsQueryOptions());

  return (
    <ConPage
      title={i18n._(msg`Fatturazione`)}
      description={i18n._(msg`Saldo, metodo di pagamento e storico delle fatture.`)}
      actions={
        <ConButton
          size="sm"
          variant="outline"
          onClick={() => portal.mutate("manage")}
          loading={portal.isPending}
        >
          <ExternalLink aria-hidden />
          <Trans>Gestisci su Stripe</Trans>
        </ConButton>
      }
    >
      <TopupReturnStatus balanceCents={credits.data?.balance_cents} />
      <BillingSummarySection />
      <WalletScopeSection />
      <PaymentMethodSection portal={portal} />
      <AccountingProfileSection />
      <CreditTopUpSection />
      <InvoicesSection />
    </ConPage>
  );
}

function WalletScopeSection(): React.ReactElement {
  const { i18n } = useLingui();
  const queryClient = useQueryClient();
  const scope = useQuery(consoleWalletScopeQueryOptions());
  const credits = useQuery(consoleCreditsQueryOptions());
  const update = useMutation({
    mutationFn: (nextScope: "shared" | "personal") => updateConsoleWalletScope(nextScope),
    onSuccess: (next) => {
      queryClient.setQueryData(consoleQueryKeys.walletScope(), next);
      void queryClient.invalidateQueries({ queryKey: consoleQueryKeys.credits() });
      consoleToast.success(i18n._(msg`Modalità wallet aggiornata`));
    },
    onError: (error) => consoleToast.error(getConsoleErrorMessage(error)),
  });
  const current = scope.data?.scope;
  return (
    <ConsoleSectionBoundary name={i18n._(msg`Wallet Engine`)}>
      <ConCard className="p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-display font-semibold">
              <Trans>Attribuzione dei crediti</Trans>
            </h2>
            <p className="mt-1 text-[color:var(--con-on-surface-muted)] text-[length:var(--con-text-sm)]">
              <Trans>
                Il saldo corrente è {formatUsdCents(i18n.locale, credits.data?.balance_cents)}.
              </Trans>
            </p>
          </div>
          {update.isPending ? (
            <ConBadge tone="neutral">
              <Trans>Salvataggio…</Trans>
            </ConBadge>
          ) : null}
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {[
            {
              value: "shared" as const,
              title: i18n._(msg`Aziendale condiviso`),
              description: i18n._(
                msg`Le nuove AppKey e le ricariche usano un unico saldo dell’organizzazione.`,
              ),
            },
            {
              value: "personal" as const,
              title: i18n._(msg`Personale per membro`),
              description: i18n._(
                msg`Le nuove AppKey e ogni ricarica usano il saldo personale del membro corrente.`,
              ),
            },
          ].map((option) => (
            <label
              key={option.value}
              className="con-wallet-option flex cursor-pointer items-start gap-3 rounded-[14px] border p-4"
            >
              <input
                className="con-wallet-radio"
                type="radio"
                name="wallet-scope"
                value={option.value}
                checked={current === option.value}
                disabled={scope.isPending || update.isPending}
                onChange={() => update.mutate(option.value)}
              />
              <span>
                <span className="block font-medium text-[length:var(--con-text-sm)]">
                  {option.title}
                </span>
                <span className="mt-1 block text-[color:var(--con-on-surface-muted)] text-[length:var(--con-text-xs)]">
                  {option.description}
                </span>
              </span>
            </label>
          ))}
        </div>
        <p className="mt-3 text-[color:var(--con-on-surface-muted)] text-[length:var(--con-text-xs)]">
          <Trans>
            Puoi cambiare modalità in qualsiasi momento. Vale per le nuove AppKey e ricariche;
            quelle esistenti mantengono il wallet originale. Nessun credito viene trasferito.
          </Trans>
        </p>
      </ConCard>
    </ConsoleSectionBoundary>
  );
}

/** Stable placeholder ids for the layout-matched summary skeleton. */
const SUMMARY_SLOT_IDS = ["b1", "b2", "b3"] as const;

/**
 * The three-tile snapshot. ``billing_mode`` picks the tile set (same 3-column
 * geometry either way): prepaid shows balance/consumed (from the credits
 * query) + the "Prepagato" plan; postpaid shows the running estimate + next
 * invoice date + "Posticipato".
 */
function BillingSummarySection(): React.ReactElement {
  const { i18n } = useLingui();
  const summary = useQuery(consoleBillingSummaryQueryOptions());
  // Prepaid balance/consumed live on the credits payload, not the summary.
  const credits = useQuery(consoleCreditsQueryOptions());

  return (
    <ConsoleSectionBoundary name={i18n._(msg`Riepilogo`)}>
      {summary.isPending ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {SUMMARY_SLOT_IDS.map((id) => (
            <div
              key={id}
              className="con-panel rounded-[16px] border border-[color:var(--con-border)] bg-[color:var(--con-surface-raised)] p-4"
            >
              <ConSkeleton className="h-3.5 w-24" />
              <ConSkeleton className="mt-3 h-7 w-20" />
            </div>
          ))}
        </div>
      ) : summary.isError ? (
        <ConErrorNote message={getConsoleErrorMessage(summary.error)} />
      ) : summary.data ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {summary.data.billing_mode === "prepaid" ? (
            <>
              <ConStat
                label={<Trans>Saldo</Trans>}
                value={formatUsdCents(i18n.locale, credits.data?.balance_cents)}
              />
              <ConStat
                label={<Trans>Consumato</Trans>}
                value={formatUsdCents(i18n.locale, credits.data?.consumed_cents)}
              />
              <ConStat label={<Trans>Piano</Trans>} value={<Trans>Prepagato</Trans>} size="sm" />
            </>
          ) : (
            <>
              <ConStat
                label={<Trans>Spesa in corso</Trans>}
                value={formatUsdCents(i18n.locale, summary.data.period_estimate_cents)}
              />
              <ConStat
                label={<Trans>Prossima fattura</Trans>}
                value={
                  summary.data.next_invoice_date
                    ? formatDate(i18n.locale, summary.data.next_invoice_date)
                    : "—"
                }
                size="sm"
              />
              <ConStat label={<Trans>Piano</Trans>} value={<Trans>Posticipato</Trans>} size="sm" />
            </>
          )}
        </div>
      ) : null}
    </ConsoleSectionBoundary>
  );
}

/**
 * Payment-method card — a status line, never the card form itself (the actual
 * card capture happens in the Stripe portal). "Gestisci" opens the portal;
 * with no method on file the body offers the orange "Aggiungi metodo" CTA.
 */
function PaymentMethodSection({ portal }: { portal: BillingPortal }): React.ReactElement {
  const { i18n } = useLingui();
  const summary = useQuery(consoleBillingSummaryQueryOptions());

  return (
    <ConsoleSectionBoundary name={i18n._(msg`Metodo di pagamento`)}>
      <ConCard>
        <ConCardHeader
          title={<Trans>Metodo di pagamento</Trans>}
          actions={
            <ConButton
              size="sm"
              variant="outline"
              onClick={() => portal.mutate("payment_method_update")}
              loading={portal.isPending}
            >
              <Trans>Gestisci</Trans>
            </ConButton>
          }
        />
        {summary.isPending ? (
          <div className="p-4">
            <ConSkeleton className="h-5 w-56" />
          </div>
        ) : summary.isError ? (
          <div className="p-4">
            <ConErrorNote message={getConsoleErrorMessage(summary.error)} />
          </div>
        ) : summary.data?.has_payment_method && summary.data.payment_method ? (
          <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="con-panel flex min-w-[240px] items-center gap-4 rounded-[16px] border border-[color:var(--con-border)] bg-[color:var(--con-surface-raised)] px-4 py-3">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-[color:var(--con-surface)]">
                <CreditCard size={20} aria-hidden className="text-[color:var(--con-accent)]" />
              </div>
              <div className="min-w-0">
                <span className="block truncate font-medium text-[color:var(--con-on-surface)] text-[length:var(--con-text-sm)] capitalize">
                  {summary.data.payment_method.type === "link"
                    ? "Link"
                    : `${summary.data.payment_method.brand ?? "Carta"} •••• ${summary.data.payment_method.last4 ?? ""}`}
                </span>
                <span className="mt-0.5 block truncate text-[color:var(--con-on-surface-muted)] text-[length:var(--con-text-xs)]">
                  {summary.data.payment_method.type === "link" ? (
                    summary.data.payment_method.email
                  ) : (
                    <>
                      <Trans>Scadenza</Trans>{" "}
                      {String(summary.data.payment_method.exp_month ?? "—").padStart(2, "0")}/
                      {summary.data.payment_method.exp_year ?? "—"}
                      {summary.data.payment_method.country
                        ? ` · ${summary.data.payment_method.country}`
                        : ""}
                    </>
                  )}
                </span>
              </div>
            </div>
            <ConBadge tone="success">
              {summary.data.payment_method.is_default ? (
                <Trans>Predefinito</Trans>
              ) : (
                <Trans>Collegato</Trans>
              )}
            </ConBadge>
          </div>
        ) : (
          <div className="flex flex-col items-start gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-[color:var(--con-on-surface-muted)] text-[length:var(--con-text-sm)]">
              <Trans>Nessun metodo di pagamento</Trans>
            </span>
            <ConButton
              onClick={() => portal.mutate("payment_method_update")}
              loading={portal.isPending}
            >
              <CreditCard aria-hidden />
              <Trans>Aggiungi metodo</Trans>
            </ConButton>
          </div>
        )}
      </ConCard>
    </ConsoleSectionBoundary>
  );
}

function AccountingProfileSection(): React.ReactElement {
  const { i18n } = useLingui();
  const summary = useQuery(consoleBillingSummaryQueryOptions());

  if (summary.isPending) return <ConSkeletonRows rows={3} />;
  if (summary.isError || !summary.data) return <></>;

  const data = summary.data;
  const address = data.billing_address;
  const addressText = address
    ? [
        address.line1,
        address.line2,
        address.postal_code,
        address.city,
        address.state,
        address.country,
      ]
        .filter(Boolean)
        .join(", ")
    : null;
  const collection =
    data.collection_method === "charge_automatically"
      ? i18n._(msg`Addebito automatico sulla carta`)
      : data.collection_method === "send_invoice"
        ? i18n._(msg`Fattura Stripe con pagamento manuale`)
        : data.collection_method === "license_contract"
          ? i18n._(msg`Contratto gestito esternamente`)
          : i18n._(msg`Predefinito della piattaforma`);
  const created = data.customer_created
    ? formatDate(i18n.locale, new Date(data.customer_created * 1000).toISOString())
    : "—";

  const rows = [
    { icon: Building2, label: i18n._(msg`Intestatario`), value: data.customer_name ?? "—" },
    { icon: Mail, label: i18n._(msg`Email di fatturazione`), value: data.billing_email ?? "—" },
    {
      icon: MapPin,
      label: i18n._(msg`Indirizzo fiscale`),
      value: addressText ?? i18n._(msg`Da completare su Stripe`),
    },
    { icon: ReceiptText, label: i18n._(msg`Riscossione`), value: collection },
  ] as const;

  return (
    <ConsoleSectionBoundary name={i18n._(msg`Profilo contabile`)}>
      <ConCard>
        <ConCardHeader
          title={<Trans>Profilo contabile</Trans>}
          description={<Trans>Dati sincronizzati dal cliente Stripe dell’organizzazione.</Trans>}
          actions={
            <ConBadge tone={data.delinquent ? "danger" : "success"}>
              {data.delinquent ? <Trans>Pagamento richiesto</Trans> : <Trans>In regola</Trans>}
            </ConBadge>
          }
        />
        <dl className="grid grid-cols-1 border-[color:var(--con-border)] border-t sm:grid-cols-2">
          {rows.map(({ icon: Icon, label, value }) => (
            <div
              key={label}
              className="flex min-w-0 gap-3 border-[color:var(--con-border)] border-b p-4 sm:odd:border-r"
            >
              <Icon
                size={16}
                aria-hidden
                className="mt-0.5 shrink-0 text-[color:var(--con-on-surface-faint)]"
              />
              <div className="min-w-0">
                <dt className="text-[color:var(--con-on-surface-muted)] text-[length:var(--con-text-xs)]">
                  {label}
                </dt>
                <dd className="mt-1 break-words text-[color:var(--con-on-surface)] text-[length:var(--con-text-sm)]">
                  {value}
                </dd>
              </div>
            </div>
          ))}
        </dl>
        <div className="grid grid-cols-2 gap-px bg-[color:var(--con-border)] sm:grid-cols-4">
          <ConStat label={<Trans>Cliente Stripe dal</Trans>} value={created} size="sm" />
          <ConStat label={<Trans>Valuta</Trans>} value={data.currency.toUpperCase()} size="sm" />
          <ConStat
            label={<Trans>Saldo Stripe</Trans>}
            value={formatUsdCents(i18n.locale, data.account_balance_cents)}
            size="sm"
          />
          <ConStat
            label={<Trans>Regime fiscale</Trans>}
            value={data.tax_exempt === "exempt" ? i18n._(msg`Esente`) : i18n._(msg`Ordinario`)}
            size="sm"
          />
        </div>
      </ConCard>
    </ConsoleSectionBoundary>
  );
}

/**
 * Prepaid credit top-up — self-serve Stripe Checkout. Prepaid tenants only
 * (``is_prepaid`` gate); hidden entirely for postpaid. The mutation, the
 * amount validation and the inline ``ConErrorNote`` are carried over verbatim
 * from the old Usage ``CreditsSection``.
 */
function CreditTopUpSection(): React.ReactElement | null {
  const { i18n } = useLingui();
  const credits = useQuery(consoleCreditsQueryOptions());
  const [amount, setAmount] = useState("20");
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const checkout = useMutation({
    mutationFn: (amountUsd: number) =>
      startConsoleCreditCheckout(
        amountUsd,
        `${window.location.origin}${window.location.pathname}`,
        topupAttemptIdempotencyKey(amountUsd),
      ),
    onMutate: () => setCheckoutError(null),
    onSuccess: (url, amountUsd) => {
      consumeTopupAttempt(amountUsd);
      if (url && url !== "#mock-checkout") window.location.href = url;
    },
    onError: (err) => setCheckoutError(getConsoleErrorMessage(err)),
  });

  if (credits.isPending || !credits.data?.is_prepaid) return null; // postpaid → hidden

  const amt = parseCustomTopupUsd(amount);
  const amtValid = amt !== null;
  const amtInvalid = amount.trim().length > 0 && !amtValid;

  return (
    <ConsoleSectionBoundary name={i18n._(msg`Crediti`)}>
      <ConCard>
        <ConCardHeader
          title={<Trans>Crediti</Trans>}
          description={<Trans>Ricarica il saldo prepagato per non interrompere il servizio.</Trans>}
        />
        <div className="flex flex-col gap-4 p-4">
          <div className="flex items-end gap-2">
            <label className="flex flex-col gap-1">
              <span className="text-[color:var(--con-on-surface-faint)] text-[length:var(--con-text-2xs)] uppercase tracking-wide">
                <Trans>Importo (USD)</Trans>
              </span>
              <ConInput
                type="number"
                inputMode="decimal"
                min={5}
                max={1000}
                step={5}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                aria-label={i18n._(msg`Importo ricarica in USD`)}
                invalid={amtInvalid}
                className="w-32 text-right tabular-nums"
              />
              {amtInvalid ? (
                <ConFieldStatus tone="danger">
                  <Trans>Inserisci un importo intero tra 5 e 1000 USD.</Trans>
                </ConFieldStatus>
              ) : (
                <ConFieldStatus>
                  <Trans>Pagamento sicuro tramite Stripe.</Trans>
                </ConFieldStatus>
              )}
            </label>
            <ConButton
              onClick={() => { if (amt !== null) checkout.mutate(amt); }}
              disabled={!amtValid}
              loading={checkout.isPending}
            >
              <Wallet aria-hidden />
              <Trans>Ricarica crediti</Trans>
            </ConButton>
          </div>
          {checkoutError ? <ConErrorNote message={checkoutError} /> : null}
        </div>
      </ConCard>
    </ConsoleSectionBoundary>
  );
}

/** Shared icon-button styling for the per-row invoice links (hosted + PDF). */
const invoiceLinkClass = cn(
  "inline-flex size-6 shrink-0 items-center justify-center rounded-sm",
  "text-[color:var(--con-on-surface-muted)] hover:bg-[color:var(--con-surface-hover)] hover:text-[color:var(--con-on-surface)]",
  "transition-colors duration-150 ease-out",
  conFocusRing,
);

/** The tenant's platform-usage invoices (Stripe collection docs) as a table. */
function InvoicesSection(): React.ReactElement {
  const { i18n } = useLingui();
  const invoices = useQuery(consoleInvoicesQueryOptions());
  const rows = invoices.data ?? [];

  return (
    <ConsoleSectionBoundary name={i18n._(msg`Fatture`)}>
      <ConCard>
        <ConCardHeader
          title={<Trans>Fatture</Trans>}
          description={<Trans>Storico delle fatture di consumo (documento di incasso).</Trans>}
        />
        {invoices.isError ? (
          <div className="p-4">
            <ConErrorNote message={getConsoleErrorMessage(invoices.error)} />
          </div>
        ) : invoices.isPending ? (
          <ConSkeletonRows rows={3} />
        ) : rows.length === 0 ? (
          <ConEmptyState
            icon={FileText}
            title={<Trans>Nessuna fattura ancora</Trans>}
            description={
              <Trans>
                Le fatture compariranno qui dopo la prima chiusura del periodo di consumo.
              </Trans>
            }
          />
        ) : (
          <ConTable ariaLabel={i18n._(msg`Storico delle fatture`)}>
            <ConTHead>
              <tr>
                <th className={conThClass}>
                  <Trans>Periodo</Trans>
                </th>
                <th className={conThClass}>
                  <Trans>Stato</Trans>
                </th>
                <th className={conThNumClass}>
                  <Trans>Importo</Trans>
                </th>
                <th className={conThClass}>
                  <span className="sr-only">
                    <Trans>Azioni</Trans>
                  </span>
                </th>
              </tr>
            </ConTHead>
            <tbody>
              {rows.map((inv) => (
                <ConTr key={inv.id}>
                  <td className={conTdClass}>
                    <span className="whitespace-nowrap">{inv.period ?? inv.id.slice(0, 12)}</span>
                  </td>
                  <td className={conTdClass}>
                    {inv.status ? <InvoiceStatusBadge status={inv.status} /> : "—"}
                  </td>
                  <td className={conTdNumClass}>{formatUsdCents(i18n.locale, inv.amount_due)}</td>
                  <td className={conTdClass}>
                    <div className="flex items-center justify-end gap-1">
                      {inv.hosted_invoice_url ? (
                        <a
                          href={inv.hosted_invoice_url}
                          target="_blank"
                          rel="noreferrer"
                          aria-label={i18n._(msg`Apri fattura`)}
                          className={invoiceLinkClass}
                        >
                          <ExternalLink size={14} aria-hidden />
                        </a>
                      ) : null}
                      <a
                        href={consoleInvoicePdfUrl(inv.id)}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={i18n._(msg`Scarica PDF`)}
                        className={invoiceLinkClass}
                      >
                        <FileDown size={14} aria-hidden />
                      </a>
                    </div>
                  </td>
                </ConTr>
              ))}
            </tbody>
          </ConTable>
        )}
      </ConCard>
    </ConsoleSectionBoundary>
  );
}
